// Confidence Meter — React port of kula-create's confidence-meter.html.
//
// Pedagogy: metacognition. Learner rates their confidence on a statement,
// reads educational content, then re-rates. The *delta* is the learning
// signal — surfaces what shifted, what didn't.
//
// Each item locks a pre/post rating and fires onActivityResponse with
//   { id, pre, post }
// so the dual-write records one learning_interactions row per item with the
// item id as activity_key (responseToActivityKey path).

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ConfidenceMeterSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { AuroraConfidenceMeter } from './AuroraConfidenceMeter';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: ConfidenceMeterSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ConfidenceMeterSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  /** Called exactly once when the learner locks the post rating on the last item. */
  onComplete?: () => void;
  /** Last recorded response payload for this slide — restores a finished state. */
  savedResponse?: Record<string, unknown> | null;
}

interface RuntimeRating {
  pre?: number;
  post?: number;
  contentRevealed?: boolean;
  postShown?: boolean;
}

export function ConfidenceMeterSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) {
    return <ConfidenceMeterEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  // Theme-world flagship #3 (THEME_WORLDS_PLAN.md): the aurora theme gets a
  // bespoke liquid-glass renderer for confidence-meter. Branch above any hooks.
  // It fires onComplete once when the last item's post rating is locked.
  // savedResponse only carries the last item's payload, which can't reconstruct
  // the full multi-item state — the bespoke renderer skips rehydration.
  if (theme.id === 'aurora') {
    return (
      <AuroraConfidenceMeter
        data={data}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onComplete={onComplete}
        savedResponse={savedResponse}
      />
    );
  }
  return (
    <ConfidenceMeterStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function ConfidenceMeterStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ConfidenceMeterSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const total = data.items.length;
  const saved: Record<string, unknown> = savedResponse ?? {};
  const lastItem = total > 0 ? data.items[total - 1] : undefined;
  // Responses are per-item ({id, pre, post}) — only the LAST item's payload proves
  // the full flow finished; an earlier item's payload can't reconstruct mid-flow
  // state, so start fresh.
  const restoredDone =
    !!lastItem &&
    saved.id === lastItem.id &&
    typeof saved.pre === 'number' &&
    typeof saved.post === 'number';

  const [currentIndex, setCurrentIndex] = useState(restoredDone ? total - 1 : 0);
  const [ratings, setRatings] = useState<Record<string, RuntimeRating>>(() =>
    restoredDone && lastItem
      ? { [lastItem.id]: { pre: saved.pre as number, post: saved.post as number, contentRevealed: true, postShown: true } }
      : {},
  );
  const completedRef = useRef(restoredDone);

  const completedCount = Object.values(ratings).filter((r) => r.post !== undefined).length;
  const allDone = restoredDone || (completedCount === total && total > 0);

  const accent = theme.colors.accent || theme.colors.primary;

  const setRating = (id: string, patch: Partial<RuntimeRating>) =>
    setRatings((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Close reflect-back: average confidence before vs after across all items.
  const avgOf = (sel: (r: RuntimeRating | undefined) => number | undefined): number | undefined => {
    const vals = data.items
      .map((i) => sel(ratings[i.id]))
      .filter((v): v is number => typeof v === 'number');
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : undefined;
  };
  const beforeAvg = avgOf((r) => r?.pre);
  const afterAvg = avgOf((r) => r?.post);
  const closeValues = {
    before: beforeAvg,
    after: afterAvg,
    shift: beforeAvg !== undefined && afterAvg !== undefined ? afterAvg - beforeAvg : undefined,
  };
  // Bare-fact default (no homily); authored `conclusion` carries the real voice.
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: 'On average your confidence moved {before} → {after}.',
    reflectionPrompt: data.summary?.prompt,
  };

  const fireComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  // Lock functions take explicit values — never re-read possibly-stale closure
  // state. (Reading r.post from the `ratings` closure here is stale and was
  // undefined when the learner confirmed WITHOUT moving the post slider — the
  // guard then bailed and the statement never advanced.)
  const lockPre = useCallback((id: string, pre: number) => {
    setRatings((prev) => ({ ...prev, [id]: { ...prev[id], pre, contentRevealed: true, postShown: true } }));
  }, []);

  const lockPost = useCallback(
    (id: string, pre: number, post: number, isLast: boolean) => {
      setRatings((prev) => ({ ...prev, [id]: { ...prev[id], post } }));
      onActivityResponse?.({
        activityType: 'confidence-meter',
        mode: 'survey',
        responseData: { id, pre, post },
      });
      if (isLast) {
        fireComplete();
      } else {
        setCurrentIndex((idx) => idx + 1);
      }
    },
    [onActivityResponse, fireComplete],
  );

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[720px] px-6 py-10">
        <header className="text-center mb-8">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        <div className="h-1 rounded-full mb-8 overflow-hidden" style={{ background: theme.colors.surface }}>
          <div
            className="h-full transition-all duration-500"
            style={{ background: accent, width: `${total > 0 ? Math.round((completedCount / total) * 100) : 0}%` }}
          />
        </div>

        {data.items.map((item, idx) => {
          if (idx > currentIndex) return null;
          const r = ratings[item.id] ?? {};
          const isCurrent = idx === currentIndex;
          const preLocked = !!r.contentRevealed;
          const postLocked = r.post !== undefined && !isCurrent;
          const isLastItem = idx === total - 1;

          return (
            <section
              key={item.id}
              className="mb-6"
              data-confidence-id={item.id}
              style={{ opacity: !isCurrent && postLocked ? 0.6 : 1 }}
            >
              {/* Each statement card rises in as the learner advances through
                  the sequence — past cards stay mounted (dimmed) for context. */}
              <Reveal variant="up" className="rounded-lg p-6 mb-4 border" style={{ background: theme.colors.surface, borderColor: `${accent}30` }}>
                <span className="inline-block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
                  Statement {idx + 1} of {total}
                </span>
                <p style={{ ...slideTypographyStyle(theme, 'subheading'), color: theme.colors.text }}>
                  {item.statement}
                </p>

                {/* Pre rating */}
                {!preLocked && isCurrent && (
                  <RatingPhase
                    label="How confident are you that this is true?"
                    item={item}
                    value={r.pre ?? Math.round((item.scale.min + item.scale.max) / 2)}
                    onChange={(v) => setRating(item.id, { pre: v })}
                    onLock={() => {
                      // Initialise pre if untouched, then lock with the explicit value.
                      const finalPre = r.pre ?? Math.round((item.scale.min + item.scale.max) / 2);
                      lockPre(item.id, finalPre);
                    }}
                    lockLabel="Lock in my rating"
                    theme={theme}
                  />
                )}

                {/* Content reveal — payoff register: heavier accent rule, larger radius, shadow. */}
                <AnimatePresence>
                  {preLocked && (
                    <Reveal
                      variant="up"
                      className="border-l-[5px] rounded-r-xl py-4 px-5 mt-4 mb-4 shadow-sm"
                      style={{ background: `${accent}15`, borderColor: accent }}
                    >
                      <span className="inline-block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
                        Did you know?
                      </span>
                      <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>
                        {item.content}
                      </p>
                    </Reveal>
                  )}
                </AnimatePresence>

                {/* Post rating */}
                {preLocked && r.post === undefined && (
                  <RatingPhase
                    label="Now how confident are you?"
                    item={item}
                    value={r.post ?? r.pre ?? Math.round((item.scale.min + item.scale.max) / 2)}
                    onChange={(v) => setRating(item.id, { post: v })}
                    onLock={() => {
                      const finalPre = r.pre ?? Math.round((item.scale.min + item.scale.max) / 2);
                      const finalPost = r.post ?? r.pre ?? Math.round((item.scale.min + item.scale.max) / 2);
                      lockPost(item.id, finalPre, finalPost, isLastItem);
                    }}
                    lockLabel={isLastItem ? 'Confirm & finish' : 'Confirm & continue'}
                    theme={theme}
                  />
                )}

                {/* Shift indicator — the metacognitive payoff. Lands with a
                    bouncy scale so the delta reads as the moment of insight. */}
                <AnimatePresence>
                  {r.pre !== undefined && r.post !== undefined && (
                    <Reveal variant="scale" spring="bouncy">
                      <ShiftIndicator pre={r.pre} post={r.post} theme={theme} />
                    </Reveal>
                  )}
                </AnimatePresence>
              </Reveal>
            </section>
          );
        })}

        {/* Authored "So what?" close — replaces the old bare Reflect prompt that
            had no input. Reflects the average confidence shift back; folds the
            legacy summary prompt into the close's optional reflection line. */}
        {allDone && (
          <Reveal variant="scale" spring="bouncy" className="mt-6">
            <ExperienceClose
              conclusion={conclusion}
              theme={theme}
              values={closeValues}
              fallback="Look back at how your confidence shifted across the statements above."
            />
          </Reveal>
        )}
      </div>
    </div>
  );
}

function RatingPhase({
  label,
  item,
  value,
  onChange,
  onLock,
  lockLabel,
  theme,
}: {
  label: string;
  item: ConfidenceMeterSlideData['items'][number];
  value: number;
  onChange: (v: number) => void;
  onLock: () => void;
  lockLabel: string;
  theme: Theme;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const pct = ((value - item.scale.min) / (item.scale.max - item.scale.min)) * 100;
  return (
    <div className="mt-4">
      <p className="text-sm font-medium mb-3" style={{ color: theme.colors.textMuted }}>
        {label}
      </p>
      <div
        className="flex justify-between text-xs mb-2"
        style={{ color: theme.colors.textMuted }}
      >
        {item.scale.labels.map((labelText, i) => (
          <span
            key={i}
            style={{ textAlign: i === 0 ? 'left' : i === item.scale.labels.length - 1 ? 'right' : 'center', flex: 1 }}
          >
            {labelText}
          </span>
        ))}
      </div>
      <input
        type="range"
        min={item.scale.min}
        max={item.scale.max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer mb-3"
        style={{
          background: `linear-gradient(to right, ${accent} 0%, ${accent} ${pct}%, ${theme.colors.textMuted}33 ${pct}%, ${theme.colors.textMuted}33 100%)`,
        }}
      />
      <div className="text-center text-2xl font-bold mb-3" style={{ color: accent }}>
        {value}
      </div>
      <button
        onClick={onLock}
        className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
        style={{ background: accent, color: '#fff' }}
      >
        {lockLabel}
      </button>
    </div>
  );
}

function ShiftIndicator({ pre, post, theme }: { pre: number; post: number; theme: Theme }) {
  const diff = post - pre;
  // + confidence → correct tier, − confidence → incorrect tier, no change → warning (neutral middle).
  if (diff === 0) {
    const neutral = feedbackStyle(theme, 'warning');
    return (
      <div className="mt-3 px-4 py-2 rounded text-sm font-semibold" style={{ background: neutral.bg, color: neutral.text }}>
        ↔ No change ({pre} → {post})
      </div>
    );
  }
  const fb = feedbackStyle(theme, diff > 0 ? 'correct' : 'incorrect');
  const arrow = diff > 0 ? '↑' : '↓';
  const label = diff > 0 ? `+${diff} more confident` : `${diff} less confident`;
  return (
    <div className="mt-3 px-4 py-2 rounded text-sm font-semibold" style={{ background: fb.bg, color: fb.text }}>
      {arrow} {label} ({pre} → {post})
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editing-mode renderer
// ---------------------------------------------------------------------------

function ConfidenceMeterEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ConfidenceMeterSlideData;
  theme: Theme;
  onUpdate: (data: ConfidenceMeterSlideData) => void;
}) {
  const updateField = <K extends keyof ConfidenceMeterSlideData>(
    key: K,
    value: ConfidenceMeterSlideData[K],
  ) => onUpdate({ ...data, [key]: value });

  const updateItem = (id: string, patch: Partial<ConfidenceMeterSlideData['items'][number]>) =>
    onUpdate({
      ...data,
      items: data.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    });

  const addItem = () => {
    onUpdate({
      ...data,
      items: [
        ...data.items,
        {
          id: crypto.randomUUID(),
          statement: '',
          content: '',
          scale: { min: 1, max: 5, labels: ['Not at all', 'Somewhat', 'Very confident'] },
        },
      ],
    });
  };

  const removeItem = (id: string) =>
    onUpdate({ ...data, items: data.items.filter((i) => i.id !== id) });

  const accent = theme.colors.accent || theme.colors.primary;

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px]">
        <input
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Confidence meter title…"
          className="w-full mb-2 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          placeholder="Subtitle"
          className="w-full mb-8 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm transition-colors"
          style={{ color: theme.colors.textMuted }}
        />

        {data.items.map((item, idx) => (
          <div
            key={item.id}
            className="rounded-lg border p-5 mb-4"
            style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold tracking-wider uppercase" style={{ color: accent }}>
                Statement {idx + 1}
              </span>
              <button onClick={() => removeItem(item.id)} className="text-xs text-gray-400 hover:text-red-500">
                Remove
              </button>
            </div>
            <input
              value={item.statement}
              onChange={(e) => updateItem(item.id, { statement: e.target.value })}
              placeholder="Statement (the claim learners rate confidence on)…"
              className="w-full mb-3 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none font-semibold"
              style={{ color: theme.colors.text }}
            />
            <textarea
              value={item.content}
              onChange={(e) => updateItem(item.id, { content: e.target.value })}
              placeholder="Revealed content (2-3 sentences that may shift the rating)"
              rows={3}
              className="w-full px-3 py-2 rounded border text-sm"
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />
          </div>
        ))}

        <button
          onClick={addItem}
          className="px-4 py-2 rounded-lg border-2 border-dashed text-sm font-medium w-full"
          style={{ borderColor: `${accent}66`, color: accent }}
        >
          + Add statement
        </button>

        <div className="mt-6">
          <label className="block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: theme.colors.textMuted }}>
            Summary prompt
          </label>
          <textarea
            value={data.summary?.prompt ?? ''}
            onChange={(e) => updateField('summary', { prompt: e.target.value })}
            placeholder="What shifted the most? Why?"
            rows={2}
            className="w-full px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.surface,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </div>
      </div>
    </div>
  );
}
